// Strategy View - strategy selection screen with vertical accordion cards
// Receives all data from ViewModel via props, no business logic

import { useState } from 'react';
import { Dispatchers } from '@client/new/helpers/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/pro-solid-svg-icons';
import { PlaybookLogo } from '@design-system/components/logo/PlaybookLogo';
import { StrategyVM, CaseStudyVM } from '../view-model/Strategy.viewModel';
import { OnboardingVMActionMap } from '../view-model/Onboarding.viewModel';
import { StrategyCard } from './components/StrategyCard';
import * as styles from './Strategy.view.css';

export type StrategyViewProps = {
  strategies: StrategyVM[];
  caseStudies: CaseStudyVM[];
  isSaving: boolean;
  isReviewMode: boolean;
} & Dispatchers<OnboardingVMActionMap>;

const getInitialExpandedId = (strategies: StrategyVM[]): string | null => {
  const recommended = strategies.find((s) => s.isRecommended);

  if (recommended) {
    return recommended.id;
  }

  return strategies[0]?.id ?? null;
};

export const StrategyView = ({
  strategies,
  caseStudies: _caseStudies,
  isSaving,
  isReviewMode,
  dispatch,
  lazyDispatch,
}: StrategyViewProps) => {
  const [expandedCardId, setExpandedCardId] = useState<string | null>(() =>
    getInitialExpandedId(strategies),
  );

  const handleSelectStrategy = (strategyId: string): void => {
    dispatch('SelectStrategy', strategyId);
  };

  const handleToggle = (strategyId: string): void => {
    setExpandedCardId((current) =>
      current === strategyId ? null : strategyId,
    );
  };

  const firstRecommended = strategies.find((s) => s.isRecommended);

  return (
    <div className={styles.container} data-testid="strategy-view">
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <PlaybookLogo showGlyph showName height={24} color="N-100" />
          {isReviewMode && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={lazyDispatch('CloseReview')}
              disabled={isSaving}
              data-testid="close-button"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faClose} />
            </button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className={styles.main}>
        {/* Title Section */}
        <section className={styles.titleSection}>
          <h1 className={styles.mainTitle}>Your winning strategy</h1>
          <p className={styles.mainSubtitle}>
            These are the best strategies, based on our experience with
            thousands of creators
          </p>
        </section>

        {/* Strategies - vertical accordion */}
        {strategies.length > 0 && (
          <section className={styles.strategiesList}>
            {strategies.map((strategy) => (
              <StrategyCard
                key={strategy.id}
                strategy={strategy}
                caseStudy={strategy.caseStudies[0] ?? null}
                isExpanded={expandedCardId === strategy.id}
                isRecommended={firstRecommended?.id === strategy.id}
                onToggle={() => handleToggle(strategy.id)}
                onSelect={handleSelectStrategy}
              />
            ))}
          </section>
        )}
      </main>

      {/* Footer with Dismiss/Clear Strategy Option */}
      <footer className={styles.footer}>
        {isReviewMode ? (
          <>
            <span className={styles.dismissText}>
              Don&apos;t feel like selecting one right now? No problem, you can
              choose one later.
            </span>
            <button
              type="button"
              className={styles.dismissButton}
              onClick={lazyDispatch('ClearStrategy')}
              disabled={isSaving}
              data-testid="clear-strategy-button"
            >
              Clear strategy
              <FontAwesomeIcon icon={faClose} className={styles.dismissIcon} />
            </button>
          </>
        ) : (
          <>
            <span className={styles.dismissText}>
              Don&apos;t feel like selecting one right now? No problem, you can
              choose one later.
            </span>
            <button
              type="button"
              className={styles.dismissButton}
              onClick={lazyDispatch('Dismiss')}
              disabled={isSaving}
              data-testid="dismiss-button"
            >
              Dismiss
              <FontAwesomeIcon icon={faClose} className={styles.dismissIcon} />
            </button>
          </>
        )}
      </footer>

      {/* Saving Overlay */}
      {isSaving && (
        <div className={styles.savingOverlay} data-testid="saving-overlay">
          <span className={styles.savingText}>Saving...</span>
        </div>
      )}
    </div>
  );
};
